// src/features/dashboard/pages/AdminDashboardPage.tsx

import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Gamepad2,
  MapPin,
  Sparkles,
  Users,
} from 'lucide-react';

import axios from 'axios';

import {
  adminDashboardService,
} from '../services/superAdminDashboardService';

import type {
  AdminDashboardData,
} from '../types/superAdminDashboard.types';


const formatNumber = (
  value: number,
): string => {
  return value.toLocaleString(
    'ko-KR',
  );
};


type ApiErrorPayload = {
  message?: string;
};


const getErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (
    axios.isAxiosError(
      error,
    )
  ) {
    const payload =
      error.response?.data as
        | ApiErrorPayload
        | undefined;

    return (
      payload?.message ||
      error.message ||
      fallback
    );
  }


  if (
    error instanceof Error
  ) {
    return (
      error.message ||
      fallback
    );
  }


  return fallback;
};


const INITIAL_DATA:
  AdminDashboardData = {
    todayVisitCount: 0,

    todayGameParticipantCount:
      0,

    dailySeries: [],

    storeGameParticipants:
      [],
  };


const AdminDashboardPage =
  () => {
    const [
      dashboard,
      setDashboard,
    ] =
      useState<AdminDashboardData>(
        INITIAL_DATA,
      );


    const [
      isLoading,
      setIsLoading,
    ] =
      useState(true);


    const [
      errorMessage,
      setErrorMessage,
    ] =
      useState('');


    /**
     * =========================================================
     * Dashboard 데이터 조회
     *
     * 페이지에서는
     * Endpoint / Axios를 알 필요가 없습니다.
     * =========================================================
     */
    useEffect(
      () => {
        let mounted =
          true;


        const loadDashboard =
          async (): Promise<void> => {
            try {
              setIsLoading(
                true,
              );

              setErrorMessage(
                '',
              );


              const data =
                await adminDashboardService
                  .getDashboard();


              if (!mounted) {
                return;
              }


              setDashboard(
                data,
              );

            } catch (
              error: unknown
            ) {
              if (!mounted) {
                return;
              }


              console.error(
                '관리자 대시보드 조회 실패:',
                error,
              );


              setDashboard(
                INITIAL_DATA,
              );


              setErrorMessage(
                getErrorMessage(
                  error,
                  '운영 통계를 불러오지 못했습니다.',
                ),
              );

            } finally {
              if (mounted) {
                setIsLoading(
                  false,
                );
              }
            }
          };


        void loadDashboard();


        return () => {
          mounted =
            false;
        };
      },
      [],
    );


    const maxVisits =
      useMemo(
        () =>
          Math.max(
            1,

            ...dashboard
              .dailySeries
              .map(
                (
                  item,
                ) =>
                  item.visits,
              ),
          ),
        [
          dashboard.dailySeries,
        ],
      );


    const maxParticipants =
      useMemo(
        () =>
          Math.max(
            1,

            ...dashboard
              .dailySeries
              .map(
                (
                  item,
                ) =>
                  item.participants,
              ),
          ),
        [
          dashboard.dailySeries,
        ],
      );


    const maxStoreJoinCount =
      useMemo(
        () =>
          Math.max(
            1,

            ...dashboard
              .storeGameParticipants
              .map(
                (
                  item,
                ) =>
                  item.joinCount,
              ),
          ),
        [
          dashboard
            .storeGameParticipants,
        ],
      );


    return (
      <div className="min-h-screen bg-gray-50 font-pretendard">

        {/* 상단 영역 */}
        <header className="relative overflow-hidden border-b border-gray-200 bg-white">

          <div
            className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-main/20 blur-3xl sm:-right-8 sm:-top-28 sm:h-64 sm:w-64"
            aria-hidden="true"
          />


          <div
            className="pointer-events-none absolute -bottom-20 -left-16 h-52 w-52 rounded-full bg-main/10 blur-3xl sm:-bottom-24 sm:left-8 sm:h-72 sm:w-72"
            aria-hidden="true"
          />


          <div className="relative mx-auto w-full max-w-6xl px-4 py-7 sm:px-6 sm:py-10 lg:px-8 lg:py-12">

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)] lg:items-center lg:gap-10">

              <div className="max-w-2xl space-y-3 sm:space-y-4">

                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600">

                  <Sparkles
                    size={14}
                    aria-hidden="true"
                  />

                  오늘의 운영 요약

                </span>


                <h1 className="break-keep text-2xl font-bold leading-tight tracking-tight text-gray-900 sm:text-3xl lg:text-4xl">
                  한 화면에서 확인하세요
                </h1>


                <p className="max-w-xl break-keep text-sm leading-6 text-gray-600 sm:text-base sm:leading-7">
                  방문자 수와 지역별 참여 현황을
                  기반으로 빠르게 의사결정을 내릴 수
                  있도록 구성했습니다.
                </p>

              </div>


              <section
                className="w-full rounded-2xl border border-gray-200 bg-white/90 p-4 shadow-sm backdrop-blur sm:rounded-3xl sm:p-5 lg:justify-self-end"
                aria-label="오늘의 운영 현황"
              >

                <div className="flex items-center justify-between gap-4">

                  <div className="min-w-0">

                    <p className="text-xs text-gray-500">
                      오늘 방문자 수
                    </p>


                    <p className="mt-1 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">

                      {formatNumber(
                        dashboard
                          .todayVisitCount,
                      )}

                      <span className="ml-1 text-base font-semibold">
                        명
                      </span>

                    </p>

                  </div>


                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-main/15 text-main sm:h-12 sm:w-12">

                    <Users
                      size={21}
                      aria-hidden="true"
                    />

                  </div>

                </div>


                <div className="mt-4 flex items-center justify-between gap-4 border-t border-gray-200 pt-4">

                  <div className="min-w-0">

                    <p className="text-xs text-gray-500">
                      오늘 게임 참가자
                    </p>


                    <p className="mt-1 text-lg font-bold text-gray-900 sm:text-xl">

                      {formatNumber(
                        dashboard
                          .todayGameParticipantCount,
                      )}

                      <span className="ml-1 text-sm font-semibold">
                        명
                      </span>

                    </p>

                  </div>


                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">

                    <Gamepad2
                      size={19}
                      aria-hidden="true"
                    />

                  </div>

                </div>

              </section>

            </div>

          </div>

        </header>


        <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-12">

          {isLoading && (

            <div className="mb-5 rounded-xl bg-white p-4 text-center text-sm text-gray-500 shadow-sm">
              운영 통계를 불러오고 있습니다.
            </div>

          )}


          {errorMessage && (

            <div
              className="mb-5 rounded-xl bg-red-50 p-4 text-sm font-medium text-red-600"
              role="alert"
            >
              {errorMessage}
            </div>

          )}


          <section className="grid items-start gap-5 sm:gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">

            {/* 최근 7일 */}
            <article className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:rounded-3xl sm:p-6">

              <div className="flex items-start justify-between gap-4">

                <div>

                  <p className="text-xs text-gray-500">
                    통계
                  </p>


                  <h2 className="mt-1 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                    일별 추이
                  </h2>

                </div>


                <span className="shrink-0 rounded-full bg-main/10 px-3 py-1 text-xs font-semibold text-main">
                  최근 7일
                </span>

              </div>


              <div className="-mx-4 mt-6 overflow-x-auto px-4 pb-2 sm:mx-0 sm:overflow-visible sm:px-0">

                <div className="grid min-w-[520px] grid-cols-7 gap-3 sm:min-w-0 sm:gap-4">

                  {dashboard
                    .dailySeries
                    .map(
                      (
                        item,
                      ) => {
                        const visitHeight =
                          Math.max(
                            item.visits >
                              0
                              ? 5
                              : 0,

                            Math.round(
                              (
                                item.visits /
                                maxVisits
                              ) *
                                100,
                            ),
                          );


                        const participantHeight =
                          Math.max(
                            item.participants >
                              0
                              ? 5
                              : 0,

                            Math.round(
                              (
                                item.participants /
                                maxParticipants
                              ) *
                                100,
                            ),
                          );


                        return (
                          <div
                            key={
                              item.date
                            }
                            className="flex min-w-0 flex-col items-center gap-2"
                          >

                            <div className="flex h-32 w-full items-end justify-center gap-1.5 sm:h-40">

                              <div
                                className="w-3 rounded-t-full bg-gradient-main transition-[height] duration-500 sm:w-4"
                                style={{
                                  height:
                                    `${visitHeight}%`,
                                }}
                                title={`방문자 ${formatNumber(
                                  item.visits,
                                )}명`}
                              />


                              <div
                                className="w-2.5 rounded-t-full bg-gray-300 transition-[height] duration-500 sm:w-3"
                                style={{
                                  height:
                                    `${participantHeight}%`,
                                }}
                                title={`참가자 ${formatNumber(
                                  item.participants,
                                )}명`}
                              />

                            </div>


                            <span className="whitespace-nowrap text-[10px] text-gray-500 sm:text-xs">
                              {
                                item.date
                              }
                            </span>

                          </div>
                        );
                      },
                    )}

                </div>

              </div>


              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 pt-4 text-xs text-gray-500">

                <span className="inline-flex items-center gap-2">

                  <span className="h-2.5 w-2.5 rounded-full bg-main" />

                  방문자 수

                </span>


                <span className="inline-flex items-center gap-2">

                  <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />

                  참가자 수

                </span>

              </div>

            </article>


            {/* 매장별 참가자 */}
            <article className="min-w-0 rounded-2xl border border-gray-800 bg-gray-900 p-4 text-white shadow-sm sm:rounded-3xl sm:p-6">

              <div className="flex items-start justify-between gap-4">

                <div>

                  <p className="text-xs text-gray-400">
                    매장별 현황
                  </p>


                  <h2 className="mt-1 text-xl font-bold tracking-tight sm:text-2xl">
                    게임 참가자 수
                  </h2>

                </div>


                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-800">

                  <MapPin
                    size={18}
                    className="text-main"
                    aria-hidden="true"
                  />

                </div>

              </div>


              <div className="mt-5 max-h-[420px] space-y-3 overflow-y-auto pr-1 sm:mt-6 sm:space-y-4 lg:max-h-[500px]">

                {dashboard
                  .storeGameParticipants
                  .length >
                0 ? (

                  dashboard
                    .storeGameParticipants
                    .map(
                      (
                        store,
                      ) => {
                        const progressWidth =
                          store.joinCount ===
                          0
                            ? 0
                            : Math.max(
                                4,

                                Math.round(
                                  (
                                    store.joinCount /
                                    maxStoreJoinCount
                                  ) *
                                    100,
                                ),
                              );


                        return (
                          <div
                            key={
                              store.storeId
                            }
                            className="rounded-xl border border-gray-700/60 bg-gray-800/80 p-3.5 sm:rounded-2xl sm:p-4"
                          >

                            <div className="flex items-center justify-between gap-3">

                              <p className="min-w-0 truncate text-sm font-semibold">
                                {
                                  store.storeName
                                }
                              </p>


                              <span className="shrink-0 text-xs text-gray-300">

                                {formatNumber(
                                  store.joinCount,
                                )}

                                명

                              </span>

                            </div>


                            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-700">

                              <div
                                className="h-full rounded-full bg-gradient-main transition-[width] duration-500"
                                style={{
                                  width:
                                    `${progressWidth}%`,
                                }}
                              />

                            </div>

                          </div>
                        );
                      },
                    )

                ) : (

                  <div className="flex min-h-36 items-center justify-center rounded-2xl border border-dashed border-gray-700 bg-gray-800/40 px-4 text-center">

                    <p className="break-keep text-sm leading-6 text-gray-400">
                      표시할 매장별 참가자 정보가 없습니다.
                    </p>

                  </div>

                )}

              </div>

            </article>

          </section>

        </main>

      </div>
    );
  };


export default AdminDashboardPage;